import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { A } from '@adornis/base/env-info.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServerOrFail } from '@adornis/bh-util/util.js';
import { ConvertConfig, FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { exportCSVFromEntities } from '@adornis/import-export/server/export-internals.js';
import { createPDFFromComponent } from '@adornis/print/server/print-internals.js';
import { format } from 'date-fns';
import { stat } from 'fs/promises';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { ExportError } from '../errors/ExportError.js';
import { mongo } from '../server/mongo.js';
import { toBerlinDate, withIdTiebreaker } from '../util.js';
import { BHFilter } from '../entities/bh-filter.js';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { Participation } from '../entities/Participation.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { ParticipationEinzelberatungExportCSV } from '../entities/ParticipationExportCSV.js';
import { Product } from '../entities/products.js';
import { User } from '../entities/user.js';
import { isWhatsappConfiguredForInstitution } from '../whatsapp/util.js';

export const exportEinzelberatungParticipantsCSV = registerQuery({
  operationName: 'exportEinzelberatungParticipantsCSV',
  type: () => String,
  params: [{ name: 'einzelberatungID', type: () => ID }],
  resolve: (einzelberatungID: string) => {
    return async () => {
      const counseling = await getEinzelberatungByID(einzelberatungID)({
        institutionID: 1,
        slots: { _id: 1, start: 1, end: 1 },
      });
      if (!counseling) return;

      const participationCollection = mongo().collection<ParticipationEinzelberatung>(
        ParticipationEinzelberatung._collectionName,
      );
      let pF = await publishFilter(ParticipationEinzelberatung._class);
      if (context.serverContext) pF = {};
      if (!pF) throw new Error(`Missing publishFilter for ${ParticipationEinzelberatung._class}`);
      const exportData = participationCollection.raw.aggregate<ParticipationEinzelberatungExportCSV>([
        { $match: { $and: [{ productID: einzelberatungID }, pF] } },
        {
          $replaceRoot: {
            newRoot: {
              participation: {
                $arrayToObject: {
                  $map: { input: { $objectToArray: '$$ROOT' }, as: 'field', in: { k: '$$field.k', v: '$$field.v' } },
                },
              },
            },
          },
        },
        {
          $lookup: {
            from: User._collectionName,
            localField: 'participation.userID',
            foreignField: '_id',
            as: 'user',
          },
        },
        { $addFields: { user: { $arrayElemAt: ['$user', 0] } } },
        {
          $lookup: {
            from: Product._collectionName,
            localField: 'participation.productID',
            foreignField: '_id',
            as: 'product',
          },
        },
        { $addFields: { product: { $arrayElemAt: ['$product', 0] } } },
        {
          $addFields: {
            slot: {
              $filter: {
                input: '$product.slots',
                as: 'slot',
                cond: { $eq: ['$$slot._id', '$participation.timeslotID'] },
              },
            },
          },
        },
        { $addFields: { slot: { $arrayElemAt: ['$slot', 0] } } },
        { $lookup: { from: User._collectionName, localField: 'slot.beraterID', foreignField: '_id', as: 'berater' } },
        { $addFields: { berater: { $arrayElemAt: ['$berater', 0] } } },
        { $lookup: { from: 'user-stammdaten', localField: 'user._id', foreignField: 'userID', as: 'stammdaten' } },
        { $unwind: '$stammdaten' },
        { $addFields: { _class: ParticipationEinzelberatungExportCSV._class } },
      ]);

      // const node = getDbPluginForScope('registration::stammdaten', counseling.institutionID).classType();
      //const dynamicFields = Object.values(node.fields);
      // TODO(diogo): Should i include the  static fields hier?

      const filePath = await exportCSVFromEntities(
        new ConvertConfig<ParticipationEinzelberatungExportCSV>({
          className: ParticipationEinzelberatungExportCSV._class,
          delimiter: ';',
          fieldMappings: [
            { input: 'ID', output: 'participation._id' },
            { input: 'Vorname', output: 'user.firstName' },
            { input: 'Nachname', output: 'user.lastName' },
            { input: 'E-Mail', output: 'user.email' },
            ...((await isWhatsappConfiguredForInstitution(counseling.institutionID))
              ? [
                  new FieldMappingEntry(
                    { input: 'WhatsApp aktiviert' },
                    undefined,
                    (v: any, data: ParticipationEinzelberatungExportCSV) =>
                      data.user?.whatsappNotification ? 'Ja' : 'Nein',
                  ),
                ]
              : []),
            { input: 'Status', output: 'participation.state' },
            new FieldMappingEntry(
              { input: 'Wartelistenplatz' },
              undefined,
              (v: any, data: ParticipationEinzelberatungExportCSV, i: number) =>
                data.participation.state === ParticipationState.waiting
                  ? (data.participation.waitingListPlace?.toString() ?? '-')
                  : '-',
            ),
            new FieldMappingEntry(
              { input: 'Letzte Änderung' },
              undefined,
              (v: any, data: ParticipationEinzelberatungExportCSV, i: number) =>
                format(data.participation.updatedAt, 'dd.MM.yyyy'),
            ),
            // ...dynamicFields.map(field => {
            //   return new FieldMappingEntry(
            //     { input: field.name },
            //     undefined,
            //     (v: any, data: ParticipationEinzelberatungExportCSV, i: number) => {
            //       const value = data.stammdaten[field.name];
            //       if (value instanceof Date) return format(value, 'dd.MM.yyyy');
            //       return data.stammdaten[field.name];
            //     },
            //   );
            // }),
            new FieldMappingEntry(
              { input: 'Berater' },
              undefined,
              (v: any, data: ParticipationEinzelberatungExportCSV, i: number) =>
                `${data.berater?.firstName} ${data.berater?.lastName}`,
            ),
            new FieldMappingEntry(
              { input: 'Zeitslot' },
              undefined,
              (v: any, data: ParticipationEinzelberatungExportCSV, i: number) => {
                const zeitslot = counseling.slots.find(slot => slot._id === data.participation.timeslotID);
                if (!zeitslot) return '---';
                if (!zeitslot.start && !zeitslot.end) return '---';
                if (zeitslot.start && !zeitslot.end) return format(toBerlinDate(zeitslot.start), 'dd.MM.yyyy HH:mm');
                if (!zeitslot.start && zeitslot.end) return format(toBerlinDate(zeitslot.end), 'dd.MM.yyyy HH:mm');
                if (zeitslot.start && zeitslot.end)
                  return `${format(toBerlinDate(zeitslot.start), 'dd.MM.yyyy HH:mm')} - ${format(toBerlinDate(zeitslot.end), 'dd.MM.yyyy HH:mm')}`;
                throw new Error('unreachable');
              },
            ),
          ],
        }),
        exportData,
        `export-teilnehmer-${A.getGloballyUniqueID()}`,
      );

      try {
        const stats = await stat(filePath);
        if (stats.size === 0) throw new Error('file is empty');
      } catch (e) {
        throw new Error('file is empty');
      }

      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  },
});

const subscribeCounselingsPaginatedResolver = function (
  this: typeof Einzelberatung,
  skip: number,
  limit: number,
  filter: BHFilter,
) {
  return (gqlFields: BaseQLSelectionSet<Einzelberatung>) => {
    if (!filter.institutionID) return [];

    return subscribePaginatedAggregation(this, skip, limit, filter.getMongoFilter(), [
      { $match: { _class: Einzelberatung._class, institutionID: filter.institutionID } },
      { $sort: withIdTiebreaker(filter.getMongoOptions().sort) },
    ]);
  };
};

export const subscribeCounselingsPaginated = registerSubscription({
  operationName: 'subscribeCounselingsPaginated',
  type: () => [Einzelberatung],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (...args: Parameters<typeof subscribeCounselingsPaginatedResolver>) =>
    subscribeCounselingsPaginatedResolver.apply(Einzelberatung, args),
});

export const removeEinzelberatungByID = registerQuery({
  operationName: 'removeEinzelberatungByID',
  type: () => String,
  params: [{ name: 'counselingID', type: () => String }],
  resolve: (counselingID: string) => {
    return async () => {
      if (context.serverContext) return;
      const contextUserID = context.userID as string;
      if (!contextUserID) throw new Error('user not logged in');
      const currentUser = await getCurrentUserServerOrFail();
      const counseling = await getEinzelberatungByID(counselingID)(selectionSet(() => Einzelberatung, 2));
      if (!counseling) throw new Error('workshop not found');
      const hasPermission = currentUser.hasPermission(
        Permission.EDIT_ADMIN_INSTITUTION_COUNSELINGS,
        counseling.institutionID,
      );
      if (!hasPermission) throw new Error('no permission to do that');
      const collectionParticipation = mongo().collection<Participation>(Participation._collectionName);

      // remove participations
      await collectionParticipation.deleteMany({ productID: counselingID });
      // remove workshop
      await counseling.remove();
    };
  },
});

const getEinzelberatungByBeraterIDResolver = function (this: typeof Einzelberatung, beraterID: string) {
  return async (gqlFields: BaseQLSelectionSet<Einzelberatung>) => {
    const collection = await scopedCollection<Einzelberatung>(this._class);
    const result = await collection
      .find<Einzelberatung>({ beraterAvailabilities: { $elemMatch: { beraterID } } })
      .toArray();
    return result;
  };
};

export const getEinzelberatungByBeraterID = registerQuery({
  operationName: 'getEinzelberatungByBeraterID',
  type: () => [Einzelberatung],
  params: [{ name: 'beraterID', type: () => String }],
  resolve: (...args: Parameters<typeof getEinzelberatungByBeraterIDResolver>) =>
    getEinzelberatungByBeraterIDResolver.apply(Einzelberatung, args),
});

const getEinzelberatungSlotCountResolver = function (this: typeof Einzelberatung, filter?: BHFilter) {
  return async () => {
    const productIDs = (() => {
      if (filter?.productID) return [filter.productID];
      // @ts-ignore
      return filter?.aspects?.find(aspect => aspect.fieldName === 'product._id')?.value?.split(',');
    })();
    const categoryIDs = (() => {
      if (filter?.categoryID) return [filter.categoryID];
      // @ts-ignore
      return filter?.aspects?.find(aspect => aspect.fieldName === 'product.category._id')?.value?.split(',');
    })();

    const aggregation = [
      {
        $match: {
          _class: this._class,
          ...(productIDs ? { _id: { $in: productIDs } } : {}),
          ...(filter?.institutionID ? { institutionID: filter.institutionID } : {}),
          ...(filter?.timeframeID ? { timeframeID: filter?.timeframeID } : {}),
          ...(categoryIDs ? { categoryID: { $in: categoryIDs } } : {}),
        },
      },
      { $project: { slotCount: { $size: '$slots' } } },
      { $group: { _id: null, totalSum: { $sum: '$slotCount' } } },
    ];

    const collection = mongo().collection<Einzelberatung>(this._collectionName);
    const result = await collection.raw.aggregate(aggregation).toArray();

    return result[0]?.totalSum ?? 0;
  };
};

export const getEinzelberatungSlotCount = registerQuery({
  operationName: 'getEinzelberatungSlotCount',
  type: () => Int,
  params: [{ name: 'filter', type: () => BHFilter }],
  resolve: (...args: Parameters<typeof getEinzelberatungSlotCountResolver>) =>
    getEinzelberatungSlotCountResolver.apply(Einzelberatung, args),
});

export const getBeraterSlotPdfLink = registerQuery({
  operationName: 'getBeraterSlotPdfLink',
  type: () => String,
  params: [{ name: 'einzelberatungID', type: () => String }],
  resolve: (einzelberatungID: string) => {
    return async () => {
      const queryParams = new URLSearchParams({ counselingID: einzelberatungID }).toString();
      const invoice = await createPDFFromComponent({
        component: 'counseling-adivsor-slots',
        timeout: 10 * 1000,
        queryParams,
        fileName: `einzelberatung_${einzelberatungID}_teilnehmerubersicht.pdf`,
      });

      return A.absoluteUrl(invoice);
    };
  },
});

export const getEinzelberatungenByIDs = registerQuery({
  operationName: 'getEinzelberatungenByIDs',
  type: () => [Einzelberatung],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Einzelberatung>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Einzelberatung, ids, gqlFields),
});

export const subscribeEinzelberatungenByIDs = registerSubscription({
  operationName: 'subscribeEinzelberatungenByIDs',
  type: () => [Einzelberatung],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Einzelberatung>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Einzelberatung, ids, gqlFields),
});

export const getAllEinzelberatungen = registerQuery({
  operationName: 'getAllEinzelberatungen',
  type: () => [Einzelberatung],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Einzelberatung>>(gqlFields: SelSet) =>
      readAllEntities(Einzelberatung, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllEinzelberatungen = registerSubscription({
  operationName: 'subscribeAllEinzelberatungen',
  type: () => [Einzelberatung],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Einzelberatung>>(gqlFields: SelSet) =>
      watchAllEntities(Einzelberatung, gqlFields, { skip, limit, excludeIDs }),
});

export const getEinzelberatungByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Einzelberatung>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getEinzelberatungenByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeEinzelberatungByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Einzelberatung>>(gqlFields: SelSet) =>
    id ? subscribeEinzelberatungenByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
